import { useState } from 'react';
import { useForm } from '@moda/portal-stanchions';
import { CONFIG } from '../../config';
import { tc } from '../../lib/trackingContext';

type ModaNetworkFormFields = {
  name: string;
  email: string;
  business: string;
  phone: string;
  link: string;
  location: string;
  message: string;
  terms: boolean;
  subscribe: boolean;
  primarySocialMediaHandle: string;
  press: string;
  estimatedClientBookValue: string;
  numOfActiveClients: string;
  affiliatePrograms: string;
  specialServices: string;
};

const VALIDATIONS = {
  name: {
    required: {
      message: 'Please enter a name.',
      value: true
    }
  },
  email: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  },
  business: {
    required: {
      message: 'Please enter a business.',
      value: true
    }
  },
  phone: {
    phoneNumber: {
      message: 'Invalid phone number.',
      value: true
    },
    required: {
      message: 'Please enter a phone number.',
      value: true
    }
  },
  link: {
    required: {
      message: 'Please enter a website.',
      value: true
    }
  },
  location: {
    required: {
      message: 'Please enter your location.',
      value: true
    }
  },
  primarySocialMediaHandle: {
    required: {
      message: 'Please enter your primary social media handle.',
      value: true
    }
  },
  press: {
    required: {
      message: 'Please enter links to notable press features.',
      value: true
    }
  },
  estimatedClientBookValue: {
    required: {
      message: 'Please select an estimated annual client book value.',
      value: true
    }
  },
  numOfActiveClients: {
    required: {
      message: 'Please select the number of active clients.',
      value: true
    }
  },
  affiliatePrograms: {
    required: {
      message: 'Please enter the affiliate programs you participate in.',
      value: true
    }
  },
  specialServices: {
    required: {
      message: 'Please enter the special services you offer.',
      value: true
    }
  }
};

const submitRequest = ({
  name,
  email,
  business,
  phone,
  link,
  location,
  message,
  subscribe,
  primarySocialMediaHandle,
  press,
  estimatedClientBookValue,
  numOfActiveClients,
  specialServices,
  affiliatePrograms
}: ModaNetworkFormFields): Promise<{ success: boolean }> =>
  fetch(`${CONFIG.MODA_API_URL}/v3.5/moda_network/network_consultation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      email,
      business,
      phone,
      link,
      location,
      message,
      subscribe,
      primary_social_media_handle: primarySocialMediaHandle,
      press,
      est_client_book_val: estimatedClientBookValue,
      num_of_active_clients: numOfActiveClients,
      special_services: specialServices,
      affiliate_programs: affiliatePrograms
    })
  }).then(response => (response.ok ? response.json() : { success: false }));

export const useModaNetworkForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: ModaNetworkFormFields) => {
    if (!values.terms) {
      setError(new Error('Please accept the Terms & Conditions.'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const { success } = await submitRequest(values);
      if (!success) throw new Error();
      if (success) {
        tc.track('Moda Network Form Submitted', {
          addToTrackingEvent: {
            name: values.name,
            email: values.email,
            business: values.business,
            phone: values.phone,
            link: values.link,
            location: values.location,
            message: values.message,
            primarySocialMediaHandle: values.primarySocialMediaHandle,
            press: values.press,
            estimatedClientBookValue: values.estimatedClientBookValue,
            numOfActiveClients: values.numOfActiveClients,
            affiliatePrograms: values.affiliatePrograms,
            specialServices: values.specialServices,
            subscribe: values.subscribe,
            terms: values.terms
          }
        });
      }
      onSuccess();
    } catch {
      setError(new Error('Unable to send your request at this time.'));
    }

    setLoading(false);
  };

  const { errors: formErrors, handleSubmit } = useForm({ validations: VALIDATIONS, onSubmit });

  return { handleSubmit, formErrors, error, loading };
};
